import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  downloadJobsData,
  downloadCandidatesData,
  getDataDownloadSettings,
  IDataDownloadSettingsDTO,
  ICvDownloadSettingsDTO,
  getCVDownloadSettings,
  downloadCV,
  ICVDownloadDTO,
} from 'wisestep-api';
import { WsButton, WsDatePicker, WsLocationSearch } from 'wisestep-base';
import { string, object } from 'yup';

import { useSKUModalStore } from '../../components/skuModal/SKUModalStore';
import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import AppSettingsLayout from '../appsettings';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

import {
  cvDownloadPayload,
  downloadHelper,
  formatter,
  jsonToFormData,
  urlAndQueryBuilderCandidates,
  urlAndQueryBuilderJobs,
} from './helper/PayloadHelper';

const DataDownload = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const validationSchema = object().shape({
    fromDate: string().required('Please enter From Date'),
    toDate: string().required('Please Enter To Date'),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {},
  };
  const methods = useForm(formOptions);
  const { pathname } = useLocation();
  const formattedDateISO = (date: Date): string => dayjs(date).toISOString();
  const dataDownloadOptions = [
    {
      label: 'Jobs',
      url: '/data-download/jobs',
    },
    {
      label: 'Candidates',
      url: '/data-download/candidates',
    },
    {
      label: 'CVs',
      url: '/data-download/cvs',
    },
  ];

  const setModal = useSKUModalStore((state) => state.setModal);

  const onDownloadHandler = async (formData: FieldValues) => {
    if (pathname === '/data-download' || pathname === '/data-download/jobs') {
      try {
        const res: IDataDownloadSettingsDTO = await (await getDataDownloadSettings()).data;

        const payload = urlAndQueryBuilderJobs(
          res.allowedColumns,
          formattedDateISO(formData.fromDate),
          formattedDateISO(formData.toDate),
          formData?.updatedFromDate && formattedDateISO(formData?.updatedFromDate),
          formData?.updatedToDate && formattedDateISO(formData?.updatedToDate)
        );

        const query = payload?.payload;

        if (query.updatedFromDate === undefined || query.updatedToDate === undefined) {
          delete query.updatedFromDate;
          delete query.updatedToDate;
        }
        downloadHelper(await downloadJobsData(payload.url, jsonToFormData(query)));
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
    if (pathname === '/data-download/candidates') {
      try {
        const res: IDataDownloadSettingsDTO = await (await getDataDownloadSettings()).data;
        const payload = urlAndQueryBuilderCandidates(
          res.allowedColumns,
          formattedDateISO(formData.fromDate),
          formattedDateISO(formData.toDate),
          formData?.updatedFromDate && formattedDateISO(formData?.updatedFromDate),
          formData?.updatedToDate && formattedDateISO(formData?.updatedToDate),
          formData?.jobLocation
        );
        const query = payload.payload;
        if (query.updatedFromDate === undefined || query.updatedToDate === undefined) {
          delete query.updatedFromDate;
          delete query.updatedToDate;
        }
        query.size = res.recordsDownloadLimit;
        downloadHelper(await downloadCandidatesData(payload.url, formatter(query)));
        setNotification(success('Your request has been submitted successfully.'));
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
    if (pathname === '/data-download/cvs') {
      try {
        const res: ICvDownloadSettingsDTO = await (await getCVDownloadSettings()).data;
        if (res.CS_CV_DOWNLOAD) {
          const payload: ICVDownloadDTO = cvDownloadPayload(
            formattedDateISO(formData.fromDate),
            formattedDateISO(formData.toDate),
            formData?.updatedFromDate && formattedDateISO(formData?.updatedFromDate),
            formData?.updatedToDate && formattedDateISO(formData?.updatedToDate)
          );
          if (payload.updatedFromDate === undefined || payload.updatedToDate === undefined) {
            payload.updatedFromDate;
            payload.updatedToDate;
          }
          try {
            await downloadCV({ ...payload });
            setNotification(
              success(
                'Your request has been submitted successfully. Workspace admin will receive a email shortly with the download link. Please note that the download link will be active for 24hours.'
              )
            );
          } catch (error: any) {
            if (error.response.status == 504) {
              setNotification(
                success(
                  'Your request for Data download is in process, we will notify you via email once the data is processed and ready to download'
                )
              );
            } else if (error.response.status == 402) {
              setModal({ object: error.response.data, type: 'count' });
            } else {
              setNotification(warning('Something went wrong. Please try again'));
            }
          }
        } else {
          setNotification(
            warning('This Feature is currently disabled for you, Please talk to Sales Team')
          );
        }
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
    methods.reset();
  };
  return (
    <AppSettingsLayout
      title="Data Download"
      options={dataDownloadOptions}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.TEAMS_AND_USERS,
        Pages['DATA_DOWNLOAD']
      )}
    >
      <FormProvider {...methods}>
        <form>
          <Grid container columnSpacing={3} rowSpacing={4}>
            {pathname === '/data-download/candidates' && (
              <>
                <Grid item xs={6}>
                  <WsLocationSearch
                    name="jobLocation"
                    placeHolder="Job Location"
                    multiSelect={true}
                  />
                </Grid>
                <Grid item xs={6}></Grid>
              </>
            )}
            <Grid item xs={6}>
              <WsDatePicker disableFuture={true} name="fromDate" label="From Date" />
            </Grid>
            <Grid item xs={6}>
              <WsDatePicker disableFuture={true} name="toDate" label="To Date" />
            </Grid>
            <Grid item xs={6}>
              <WsDatePicker disableFuture={true} name="updatedFromDate" label="Updated From Date" />
            </Grid>
            <Grid item xs={6}>
              <WsDatePicker disableFuture={true} name="updatedToDate" label="Updated To Date" />
            </Grid>
            <Grid item xs={12}>
              <WsButton onClick={methods.handleSubmit(onDownloadHandler)} type="submit">
                Download
              </WsButton>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </AppSettingsLayout>
  );
};

export default DataDownload;
